<template>
  <article
    class="bg-white border border-l-4 border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow text-left"
    :style="{ borderLeftColor: facultyInfo.color }"
  >
    <div class="flex items-start gap-3">
      <peer-avatar :user="user" size="lg" />
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2">
          <button type="button" class="block max-w-full min-w-0 text-left" @click="$emit('view', user)">
          <h3 class="font-semibold text-gray-900 truncate">{{ user.name || 'Student' }}</h3>
          <p class="text-sm text-gray-500 truncate">{{ yearText }}</p>
          </button>
          <span
            class="px-2 py-1 rounded-full text-[11px] font-semibold shrink-0"
            :style="facultyBadgeStyle"
            :title="facultyInfo.name"
          >
            {{ facultyInfo.code }}
          </span>
        </div>
        <p v-if="primaryInfo" class="mt-2 text-sm text-gray-700 truncate">{{ primaryInfo }}</p>
        <p v-if="secondaryInfo" class="text-xs text-gray-500 truncate">{{ secondaryInfo }}</p>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-2 gap-2">
      <button
        type="button"
        class="px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
        :class="connectionButtonClass"
        @click="$emit('connect', user.id)"
      >
        <span class="material-symbols-outlined text-base">{{ connectionIcon }}</span>
        <span>{{ connectionButtonText }}</span>
      </button>
      <button
        type="button"
        class="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
        @click="$emit('view', user)"
      >
        <span class="material-symbols-outlined text-base">visibility</span>
        <span>View</span>
      </button>
      <button
        v-if="showReject && connectionStatus === 'request'"
        type="button"
        class="col-span-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
        @click="$emit('reject', user.id)"
      >
        <span class="material-symbols-outlined text-base">close</span>
        <span>Reject</span>
      </button>
      <button
        v-if="connectionStatus === 'connected'"
        type="button"
        class="col-span-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
        @click="$emit('chat', user.id)"
      >
        <span class="material-symbols-outlined text-base">chat</span>
        <span>Chat</span>
      </button>
    </div>
  </article>
</template>

<script>
import PeerAvatar from './PeerAvatar.vue';
import { useResources } from '@/composables/useResources';

export default {
  name: 'PeerProfileCard',
  components: { PeerAvatar },
  setup() {
    const { getFacultyInfo } = useResources();
    return { getFacultyInfo };
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    connectionStatus: {
      type: String,
      default: null
    },
    showReject: {
      type: Boolean,
      default: false
    }
  },
  emits: ['connect', 'chat', 'view', 'reject'],
  computed: {
    facultyInfo() {
      return this.getFacultyInfo(this.user.university?.faculty);
    },
    facultyBadgeStyle() {
      return {
        color: this.facultyInfo.color,
        backgroundColor: `${this.facultyInfo.color}14`
      };
    },
    yearText() {
      const year = this.user.university?.studyYear;
      if (!year) return 'Student';
      const yearNum = parseInt(year);
      if (Number.isNaN(yearNum)) return year;
      const suffixes = ['st', 'nd', 'rd'];
      const suffix = yearNum <= 3 ? suffixes[yearNum - 1] : 'th';
      return `${yearNum}${suffix} Year Student`;
    },
    primaryInfo() {
      if (this.user.privacySettings?.showCourseName === false) return this.user.university?.faculty || '';
      return this.user.university?.courseName || this.user.university?.faculty || '';
    },
    secondaryInfo() {
      if (this.user.privacySettings?.showFaculty === false) return this.user.university?.name || '';
      return this.user.university?.faculty || this.user.university?.name || '';
    },
    connectionButtonText() {
      switch (this.connectionStatus) {
        case 'connected': return 'Connected';
        case 'pending': return 'Pending';
        case 'request': return 'Accept';
        default: return 'Connect';
      }
    },
    connectionIcon() {
      switch (this.connectionStatus) {
        case 'connected': return 'how_to_reg';
        case 'pending': return 'pending';
        case 'request': return 'person_add';
        default: return 'person_add';
      }
    },
    connectionButtonClass() {
      switch (this.connectionStatus) {
        case 'connected': return 'bg-green-100 text-green-700 hover:bg-green-200';
        case 'pending': return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
        case 'request': return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
        default: return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
      }
    }
  }
};
</script>
